import React, { useState, useEffect } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  useToast,
  InputRightElement,
  InputGroup,
  Textarea,
  Box,
  Heading,
  Divider,
  HStack,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';

const NewClubForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    features: {
      voiceChat: false,
      chatbot: false,
    },
    voiceBots: [],
    chatBots: [],
    memberCount: 0,
    totalTokens: 0,
    email: '',
    city: '',
    country: '',
    phone: '',
    website: '',
    services: [],
    needs: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    password: '',
    confirmPassword: '',
  });

  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Generate a random password
  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // Set generated password when component mounts
  useEffect(() => {
    const generatedPassword = generatePassword();
    setFormData(prev => ({
      ...prev,
      password: generatedPassword,
      confirmPassword: generatedPassword
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const addBot = (type) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Bots`]: [
        ...prev[`${type}Bots`],
        {
          name: '',
          apiKey: '',
          agentId: '',
          projectId: '',
          status: 'active',
          ...(type === 'chat' ? { projectIds: [] } : {})
        }
      ]
    }));
  };

  const removeBot = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Bots`]: prev[`${type}Bots`].filter((_, i) => i !== index)
    }));
  };

  const updateBot = (type, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Bots`]: prev[`${type}Bots`].map((bot, i) => 
        i === index ? { ...bot, [field]: value } : bot
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.email || !formData.password) {
      toast({
        title: 'Error',
        description: 'Name, email, and password are required.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate password
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const dataToSend = { ...formData };
      await onSubmit(dataToSend);
    } catch (error) {
      console.error('Error in NewClubForm submit:', error);
      toast({
        title: 'Error creating user',
        description: error.response?.data?.message || error.message || 'Failed to create user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="md" mb={4}>Basic Information</Heading>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>User Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter user name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter user description"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter user email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter user phone"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Website</FormLabel>
              <Input
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="Enter user website"
              />
            </FormControl>

            <FormControl>
              <FormLabel>City</FormLabel>
              <Input
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Enter city"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Country</FormLabel>
              <Input
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Enter country"
              />
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Contact Information</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Contact Name</FormLabel>
              <Input
                name="contactName"
                value={formData.contactName}
                onChange={handleInputChange}
                placeholder="Enter contact name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Contact Email</FormLabel>
              <Input
                name="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="Enter contact email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Contact Phone</FormLabel>
              <Input
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="Enter contact phone"
              />
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Account Settings</Heading>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Generated Password</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Generated password"
                  isReadOnly
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Confirm Password</FormLabel>
              <InputGroup>
                <Input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm password"
                  isReadOnly
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </Select>
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Features & Services</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Features</FormLabel>
              <VStack align="start" spacing={2}>
                <Checkbox
                  isChecked={formData.features.voiceChat}
                  onChange={() => handleFeatureChange('voiceChat')}
                >
                  Voice Chat
                </Checkbox>
                <Checkbox
                  isChecked={formData.features.chatbot}
                  onChange={() => handleFeatureChange('chatbot')}
                >
                  Chatbot
                </Checkbox>
              </VStack>
            </FormControl>

            <FormControl>
              <FormLabel>Services (comma-separated)</FormLabel>
              <Input
                name="services"
                value={Array.isArray(formData.services) ? formData.services.join(', ') : formData.services}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  services: e.target.value.split(',').map(service => service.trim())
                }))}
                placeholder="Enter services (e.g., ticket booking, customer support)"
              />
            </FormControl>

            <FormControl>
              <FormLabel>user Needs</FormLabel>
              <Textarea
                name="needs"
                value={formData.needs}
                onChange={handleInputChange}
                placeholder="Describe your user's specific needs"
              />
            </FormControl>
          </VStack>
        </Box>

        {formData.features.voiceChat && (
          <>
            <Divider />
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Voice Bots</Heading>
                <Button leftIcon={<AddIcon />} size="sm" onClick={() => addBot('voice')}>
                  Add Voice Bot
                </Button>
              </Flex>
              <VStack spacing={4}>
                {formData.voiceBots.map((bot, index) => (
                  <Box key={index} p={4} borderWidth={1} borderRadius="md" width="100%">
                    <Flex justify="space-between" mb={2}>
                      <Heading size="sm">Voice Bot {index + 1}</Heading>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => removeBot('voice', index)}
                      />
                    </Flex>
                    <VStack spacing={3}>
                      <FormControl>
                        <FormLabel>Bot Name</FormLabel>
                        <Input
                          value={bot.name}
                          onChange={(e) => updateBot('voice', index, 'name', e.target.value)}
                          placeholder="Enter bot name"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>API Key</FormLabel>
                        <Input
                          value={bot.apiKey}
                          onChange={(e) => updateBot('voice', index, 'apiKey', e.target.value)}
                          placeholder="Enter API Key"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Agent ID</FormLabel>
                        <Input
                          value={bot.agentId}
                          onChange={(e) => updateBot('voice', index, 'agentId', e.target.value)}
                          placeholder="Enter Agent ID"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Project ID</FormLabel>
                        <Input
                          value={bot.projectId}
                          onChange={(e) => updateBot('voice', index, 'projectId', e.target.value)}
                          placeholder="Enter Project ID"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={bot.status}
                          onChange={(e) => updateBot('voice', index, 'status', e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Select>
                      </FormControl>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          </>
        )}

        {formData.features.chatbot && (
          <>
            <Divider />
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Chat Bots</Heading>
                <Button leftIcon={<AddIcon />} size="sm" onClick={() => addBot('chat')}>
                  Add Chat Bot
                </Button>
              </Flex>
              <VStack spacing={4}>
                {formData.chatBots.map((bot, index) => (
                  <Box key={index} p={4} borderWidth={1} borderRadius="md" width="100%">
                    <Flex justify="space-between" mb={2}>
                      <Heading size="sm">Chat Bot {index + 1}</Heading>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => removeBot('chat', index)}
                      />
                    </Flex>
                    <VStack spacing={3}>
                      <FormControl>
                        <FormLabel>Bot Name</FormLabel>
                        <Input
                          value={bot.name}
                          onChange={(e) => updateBot('chat', index, 'name', e.target.value)}
                          placeholder="Enter bot name"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>API Key</FormLabel>
                        <Input
                          value={bot.apiKey}
                          onChange={(e) => updateBot('chat', index, 'apiKey', e.target.value)}
                          placeholder="Enter API Key"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Agent ID</FormLabel>
                        <Input
                          value={bot.agentId}
                          onChange={(e) => updateBot('chat', index, 'agentId', e.target.value)}
                          placeholder="Enter Agent ID"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Project IDs (comma-separated)</FormLabel>
                        <Input
                          value={Array.isArray(bot.projectIds) ? bot.projectIds.join(', ') : bot.projectIds}
                          onChange={(e) => updateBot('chat', index, 'projectIds', e.target.value.split(',').map(id => id.trim()))}
                          placeholder="Enter Project IDs"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={bot.status}
                          onChange={(e) => updateBot('chat', index, 'status', e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Select>
                      </FormControl>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          </>
        )}

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Usage Statistics</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Member Count</FormLabel>
              <NumberInput
                name="memberCount"
                value={formData.memberCount}
                onChange={(valueString) => setFormData(prev => ({ ...prev, memberCount: parseInt(valueString) }))}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Total Tokens</FormLabel>
              <NumberInput
                name="totalTokens"
                value={formData.totalTokens}
                onChange={(valueString) => setFormData(prev => ({ ...prev, totalTokens: parseInt(valueString) }))}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </VStack>
        </Box>

        <HStack spacing={4} justify="flex-end">
          <Button onClick={onCancel}>Cancel</Button>
          <Button colorScheme="teal" type="submit">
            Create user
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};

export default NewClubForm; 